import * as React from 'react';
import { Avatar, List, Button, Popconfirm } from 'antd';
import {
  RedoOutlined,
  SyncOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import PhotoViewer from '../../../Component/PhotoViewer';

export interface SyncQueueItemData {
  data: any;
  idbId: number;
  status: 'pending'|'in-progress'|'error'|'canceled'|'done'|'deleted';
  error?: string;
}

interface SyncQueueItemProps extends SyncQueueItemData {
  onSync?: Function;
  onDelete?: Function;
  treetypesList: any;
  language?: string;
  /** Whether the full screen viewer was last opened on this tree's photo. */
  highlighted?: boolean;
  /** Called when the thumbnail is clicked, so the list can mark this row and unmark the others. */
  onPhotoClick?: Function;
}

export default class SyncQueueItem extends React.Component<SyncQueueItemProps, any>{
  constructor(props: SyncQueueItemProps) {
    super(props);
    this.state = {
      photoUrl: undefined,
      // Whether the full screen viewer is open on this tree photo.
      viewerOpen: false
    };
  }

  componentDidMount() {
    this.updatePhotoUrl();
  }

  componentDidUpdate(prevProps: SyncQueueItemProps) {
    if (prevProps.data.photo !== this.props.data.photo) {
      this.updatePhotoUrl();
    }
  }

  componentWillUnmount() {
    this.revokePhotoUrl();
  }

  updatePhotoUrl() {
    const { data } = this.props;
    this.revokePhotoUrl();

    if (data && data.photo) {
      if (data.photo instanceof Blob || data.photo instanceof File) {
        const url = URL.createObjectURL(data.photo);
        this.setState({ photoUrl: url });
      } else if (typeof data.photo === 'string') {
        this.setState({ photoUrl: data.photo });
      }
    }
  }

  revokePhotoUrl() {
    const { photoUrl } = this.state;
    if (photoUrl && typeof photoUrl === 'string' && photoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(photoUrl);
    }
  }

  getStatusIcon() {
    const { status } = this.props;

    switch (status) {
      case 'in-progress':
        return <SyncOutlined spin />;
      case 'error':
        return <RedoOutlined />;
      case 'pending':
      default:
        return <SyncOutlined />;
    }
  }

  getActionButton(icon: React.ReactNode, text: string, danger: boolean, disabled: boolean, callback?: Function) {
    const { idbId } = this.props;

    return <Button
      icon={icon}
      type={'link'}
      onClick={async () => callback && await callback(idbId)}
      key={"list-" + text}
      danger={danger}
    >
      {text}
    </Button>
  }

  getTitle(id: number, numberPlanted: number, translations: any) {
    let description = 'no description';

    try {
      const { language } = this.props;
      description = language ? translations[language] : 'no description';

      // @ts-ignore
      return `#${id} - ${numberPlanted}x ${description} (${translations['latin']})`;
    } catch (err) {
      console.error(err);
      return `#${id} - ${description}`;
    }
  }

  getTranslations() {
    try {
      const { treetypesList, data } = this.props;

      const treeTypeData = treetypesList.filter((item: any) => {
        const intType = parseInt(data.typeId);
        return item.id === intType
      });
      const filteredTreeTypeData = treeTypeData[0];
      return filteredTreeTypeData.translations;
    } catch (err) {
      console.error(err);
      return undefined;
    }
  }

  render() {
    const { data, idbId, status, onSync, onDelete, highlighted, onPhotoClick } = this.props;
    const { photoUrl, viewerOpen } = this.state;

    if (status === 'done'|| status === 'deleted') {
      return null;
    }

    // The thumbnail opens the photo full screen through the same viewer - and the same fade in/out - as
    // the tree popups. There is nothing to open when the tree has no readable photo. The row is boxed on
    // the click, before the viewer fades in, and stays boxed afterwards so the tree that was opened is
    // recognisable once the viewer is closed.
    return <List.Item
      key={idbId}
      style={highlighted ? {
        // The rectangle antd draws on a focused field: its 1px #40a9ff border and the 2px
        // rgba(24,144,255,.2) halo that goes around it. Both are drawn inside the row, because a ring
        // laid around it would be cut off by the edges of the modal - the halo therefore starts where
        // the border ends.
        boxShadow: 'inset 0 0 0 1px #40a9ff, inset 0 0 0 3px rgba(24, 144, 255, 0.2)',
        borderRadius: '4px',
      } : undefined}
    >
      <List.Item.Meta
        avatar={<span
          onClick={() => {
            if (!photoUrl) {
              return;
            }
            onPhotoClick && onPhotoClick(idbId);
            this.setState({ viewerOpen: true });
          }}
          style={{
            cursor: photoUrl ? 'pointer' : 'default',
            display: 'inline-block',
            // Keeps the photo clear of the box drawn around the row (see above). Every row is shifted the
            // same way, so the list stays aligned whether a row is boxed or not.
            marginLeft: '8px',
          }}
        >
          <Avatar src={photoUrl} size="large" shape="square" />
        </span>}
        title={this.getTitle(idbId, data.number, this.getTranslations())}
        description={
          <>
            {this.getActionButton(this.getStatusIcon(), 'sync', false, status === 'in-progress', onSync)}
            <Popconfirm
              placement="top"
              title={'Delete this tree ?'}
              onConfirm={async () => onDelete && await onDelete(idbId)}
              okText="Yes, delete"
              cancelText="Cancel"
            >
              {this.getActionButton(<CloseCircleOutlined />, 'delete', true, status !== 'in-progress')}
            </Popconfirm>
          </>
        }
      />
      <PhotoViewer
        url={viewerOpen ? photoUrl : undefined}
        onClose={() => this.setState({ viewerOpen: false })}
      />
    </List.Item>
  }
}